import axios from 'axios';
import { enqueue } from '../utils/offlineQueue';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// Helper to read a cookie value by name
function getCookie(name) {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match ? match[2] : null;
}

// Add request interceptor for CSRF token and FormData handling
api.interceptors.request.use(
  (config) => {
    // Attach CSRF token from cookie for state-changing requests
    const csrfToken = getCookie('csrf_access_token');
    if (csrfToken) {
      config.headers['X-CSRF-TOKEN'] = csrfToken;
    }
    // If the request data is FormData, delete the Content-Type header
    // to let the browser set it with the proper boundary
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Token refresh state
let isRefreshing = false;
let refreshSubscribers = [];
const onRefreshed = () => { refreshSubscribers.forEach(cb => cb()); refreshSubscribers = []; };
const addRefreshSubscriber = (cb) => { refreshSubscribers.push(cb); };

/** Pathname for refresh/login detection (handles absolute request URLs). */
function requestPathname(config) {
  if (!config || !config.url) return '';
  const raw = String(config.url).split('?')[0];
  if (/^https?:\/\//i.test(raw)) {
    try {
      return new URL(raw).pathname;
    } catch {
      return raw;
    }
  }
  return raw.startsWith('/') ? raw : `/${raw}`;
}

function isAuthRefreshOrLoginRequest(config) {
  const path = requestPathname(config);
  return /\/auth\/refresh$/.test(path) || /\/auth\/login$/.test(path);
}

/** Body for offline queue: plain objects or JSON strings; skip FormData/Blob. */
function parseRequestDataForQueue(data) {
  if (data == null || data === '') return null;
  if (typeof FormData !== 'undefined' && data instanceof FormData) return undefined;
  if (typeof Blob !== 'undefined' && data instanceof Blob) return undefined;
  if (typeof data === 'object') return data;
  if (typeof data === 'string') {
    try {
      return JSON.parse(data);
    } catch {
      return undefined;
    }
  }
  return undefined;
}

// Handle 401 responses — attempt token refresh before redirecting to login
api.interceptors.response.use(
  (response) => {
    // Track last activity for session timeout
    localStorage.setItem('last_activity', Date.now().toString());
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    if (!originalRequest) {
      return Promise.reject(error);
    }
    if (error.response && error.response.status === 401 && !originalRequest._retry) {
      // Don't try to refresh if this IS the refresh request, or login request
      if (isAuthRefreshOrLoginRequest(originalRequest)) {
        localStorage.removeItem('user');
        window.location.href = '/login';
        return Promise.reject(error);
      }
      originalRequest._retry = true;
      if (!isRefreshing) {
        isRefreshing = true;
        try {
          await api.post('/auth/refresh');
          isRefreshing = false;
          onRefreshed();
          return api(originalRequest);
        } catch {
          isRefreshing = false;
          refreshSubscribers = [];
          localStorage.removeItem('user');
          window.location.href = '/login';
          return Promise.reject(error);
        }
      } else {
        // Queue request until refresh completes
        return new Promise((resolve) => {
          addRefreshSubscriber(() => {
            resolve(api(originalRequest));
          });
        });
      }
    }
    // Queue mutating requests when offline (network error, no response)
    if (!error.response && !navigator.onLine && error.config) {
      const method = (error.config.method || '').toUpperCase();
      const relUrl = String(error.config.url || '');
      if (['POST', 'PUT', 'DELETE'].includes(method) && !relUrl.includes('/auth/')) {
        const payload = parseRequestDataForQueue(error.config.data);
        if (payload === undefined && error.config.data != null && error.config.data !== '') {
          return Promise.reject(error);
        }
        let fullUrl;
        try {
          fullUrl = typeof api.getUri === 'function' ? api.getUri(error.config) : null;
        } catch {
          fullUrl = null;
        }
        if (!fullUrl) {
          const base = String(API_BASE_URL || '').replace(/\/$/, '');
          const pathOnly = relUrl.split('?')[0];
          fullUrl = /^https?:\/\//i.test(pathOnly)
            ? pathOnly
            : `${base}/${pathOnly.replace(/^\//, '')}`;
        }
        // Include the CSRF header so the replayed request isn't rejected on
        // reconnect — flask-jwt-extended's CSRF check applies to queued
        // mutations just like it did to the original request.
        const csrfToken = getCookie('csrf_access_token');
        const queueHeaders = csrfToken
          ? { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': csrfToken }
          : { 'Content-Type': 'application/json' };
        enqueue(method, fullUrl.split('?')[0], payload ?? null, queueHeaders).catch(() => {});
        return Promise.reject(Object.assign(error, { queued: true }));
      }
    }
    return Promise.reject(error);
  }
);

// Cattle API
export const cattleAPI = {
  getAll: () => api.get('/cattle'),
  getById: (id) => api.get(`/cattle/${id}`),
  create: (data) => api.post('/cattle', data),
  update: (id, data) => api.put(`/cattle/${id}`, data),
  delete: (id) => api.delete(`/cattle/${id}`),
  getProgeny: (id) => api.get(`/cattle/${id}/progeny`),
  getParentLinkAudit: () => api.get('/cattle/parent-link-audit'),
};

// Vaccination API
export const vaccinationAPI = {
  getAll: () => api.get('/vaccinations'),
  getByCattleId: (cattleId) => api.get(`/cattle/${cattleId}/vaccinations`),
  create: (data) => api.post('/vaccinations', data),
  update: (id, data) => api.put(`/vaccinations/${id}`, data),
  delete: (id) => api.delete(`/vaccinations/${id}`),
};

// Pasture API
export const pastureAPI = {
  getAll: () => api.get('/pastures'),
  getById: (id) => api.get(`/pastures/${id}`),
  create: (data) => api.post('/pastures', data),
  update: (id, data) => api.put(`/pastures/${id}`, data),
  delete: (id) => api.delete(`/pastures/${id}`),
};

// Pasture Assignment API
export const pastureAssignmentAPI = {
  getAll: () => api.get('/pasture-assignments'),
  getByCattleId: (cattleId) => api.get(`/cattle/${cattleId}/pasture-assignments`),
  getByPastureId: (pastureId) => api.get(`/pastures/${pastureId}/assignments`),
  create: (data) => api.post('/pasture-assignments', data),
  update: (id, data) => api.put(`/pasture-assignments/${id}`, data),
  delete: (id) => api.delete(`/pasture-assignments/${id}`),
};

// Pasture Maintenance API
export const pastureMaintenanceAPI = {
  getAll: () => api.get('/pasture-maintenance'),
  getByPastureId: (pastureId) => api.get(`/pastures/${pastureId}/maintenance`),
  create: (data) => api.post('/pasture-maintenance', data),
  delete: (id) => api.delete(`/pasture-maintenance/${id}`),
};

// Vet Visit API
export const vetVisitAPI = {
  getAll: () => api.get('/vet-visits'),
  getById: (id) => api.get(`/vet-visits/${id}`),
  getByCattleId: (cattleId) => api.get(`/cattle/${cattleId}/vet-visits`),
  create: (data) => api.post('/vet-visits', data),
  update: (id, data) => api.put(`/vet-visits/${id}`, data),
  delete: (id) => api.delete(`/vet-visits/${id}`),
};

// Feeding Record API
export const feedingRecordAPI = {
  getAll: () => api.get('/feeding-records'),
  getByCattleId: (cattleId) => api.get(`/cattle/${cattleId}/feeding-records`),
  create: (data) => api.post('/feeding-records', data),
  update: (id, data) => api.put(`/feeding-records/${id}`, data),
  delete: (id) => api.delete(`/feeding-records/${id}`),
};

// Cattle Photo API
export const cattlePhotoAPI = {
  getByCattleId: (cattleId) => api.get(`/cattle/${cattleId}/photos`),
  upload: (cattleId, formData) => api.post(`/cattle/${cattleId}/photos`, formData),
  getPhotoUrl: (photoId) => `${API_BASE_URL}/cattle/photos/${photoId}`,
  getPhotoBlob: async (photoId) => {
    const response = await api.get(`/cattle/photos/${photoId}`, {
      responseType: 'blob'
    });
    return URL.createObjectURL(response.data);
  },
  update: (photoId, data) => api.put(`/cattle/photos/${photoId}`, data),
  delete: (photoId) => api.delete(`/cattle/photos/${photoId}`),
};

// Breeding Record API
export const breedingRecordAPI = {
  getAll: () => api.get('/breeding-records'),
  getByCattleId: (cattleId) => api.get(`/cattle/${cattleId}/breeding-records`),
  create: (data) => api.post('/breeding-records', data),
  update: (id, data) => api.put(`/breeding-records/${id}`, data),
  delete: (id) => api.delete(`/breeding-records/${id}`),
};

// FeedLot API
export const feedLotAPI = {
  getAll: () => api.get('/feedlots'),
  getById: (id) => api.get(`/feedlots/${id}`),
  create: (data) => api.post('/feedlots', data),
  update: (id, data) => api.put(`/feedlots/${id}`, data),
  delete: (id) => api.delete(`/feedlots/${id}`),
};

// Hay Field API
export const hayFieldAPI = {
  getAll: () => api.get('/hay-fields'),
  getById: (id) => api.get(`/hay-fields/${id}`),
  create: (data) => api.post('/hay-fields', data),
  update: (id, data) => api.put(`/hay-fields/${id}`, data),
  delete: (id) => api.delete(`/hay-fields/${id}`),
};

// Hay Production API
export const hayProductionAPI = {
  getByHayFieldId: (hayFieldId) => api.get(`/hay-fields/${hayFieldId}/production`),
  create: (data) => api.post('/hay-production', data),
  delete: (id) => api.delete(`/hay-production/${id}`),
};

// Equipment API
export const equipmentAPI = {
  getAll: () => api.get('/equipment'),
  getById: (id) => api.get(`/equipment/${id}`),
  create: (data) => api.post('/equipment', data),
  update: (id, data) => api.put(`/equipment/${id}`, data),
  delete: (id) => api.delete(`/equipment/${id}`),
};

// Equipment Maintenance API
export const equipmentMaintenanceAPI = {
  getByEquipmentId: (equipmentId) => api.get(`/equipment/${equipmentId}/maintenance`),
  create: (data) => api.post('/equipment-maintenance', data),
  delete: (id) => api.delete(`/equipment-maintenance/${id}`),
};

// Weight Record API
export const weightRecordAPI = {
  getByCattleId: (cattleId) => api.get(`/cattle/${cattleId}/weight-records`),
  create: (data) => api.post('/weight-records', data),
  update: (id, data) => api.put(`/weight-records/${id}`, data),
  delete: (id) => api.delete(`/weight-records/${id}`),
};

// Medication (Inventory) API
export const medicationAPI = {
  getAll: () => api.get('/medications'),
  getById: (id) => api.get(`/medications/${id}`),
  create: (data) => api.post('/medications', data),
  update: (id, data) => api.put(`/medications/${id}`, data),
  delete: (id) => api.delete(`/medications/${id}`),
};

// Treatment Record API
export const treatmentRecordAPI = {
  getByCattleId: (cattleId) => api.get(`/cattle/${cattleId}/treatments`),
  create: (data) => api.post('/treatment-records', data),
  delete: (id) => api.delete(`/treatment-records/${id}`),
};

// Calving Record API
export const calvingRecordAPI = {
  getAll: () => api.get('/calving-records'),
  getByCattleId: (cattleId) => api.get(`/cattle/${cattleId}/calving-records`),
  create: (data) => api.post('/calving-records', data),
  update: (id, data) => api.put(`/calving-records/${id}`, data),
  delete: (id) => api.delete(`/calving-records/${id}`),
};

// Notification API
export const notificationAPI = {
  getAll: (unreadOnly) => api.get(`/notifications${unreadOnly ? '?unread_only=true' : ''}`),
  getUnreadCount: () => api.get('/notifications/unread-count'),
  markRead: (id) => api.put(`/notifications/${id}/read`),
  markAllRead: () => api.put('/notifications/read-all'),
};

// Analytics API
export const analyticsAPI = {
  getHerdSummary: () => api.get('/analytics/herd-summary'),
  getBreedingPerformance: () => api.get('/analytics/breeding-performance'),
  getFinancialSummary: () => api.get('/analytics/financial-summary'),
  getDashboardCharts: () => api.get('/analytics/dashboard-charts'),
};

// Export API
export const exportAPI = {
  cattle: () => api.get('/export/cattle', { responseType: 'blob' }),
  vetVisits: () => api.get('/export/vet-visits', { responseType: 'blob' }),
  breedingRecords: () => api.get('/export/breeding-records', { responseType: 'blob' }),
  equipment: () => api.get('/export/equipment', { responseType: 'blob' }),
};

// Import API
export const importAPI = {
  cattle: (formData) => api.post('/import/cattle', formData),
};

// Search API
export const searchAPI = {
  global: (query) => api.get(`/search?q=${encodeURIComponent(query)}`),
};

// Gestation Calculator
export const breedingUtilsAPI = {
  calculateDueDate: (id) => api.post(`/breeding-records/${id}/calculate-due-date`),
};

// Vaccination Alert API
export const vaccinationAlertAPI = {
  checkDue: (days) => api.get(`/vaccinations/check-due?days=${days || 30}`),
};

// Password Reset API (no auth needed)
export const passwordResetAPI = {
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, password) => api.post('/auth/reset-password', { token, password }),
};

// ── Auth API (consolidated from authApi.js fetch client) ────
// User info management (non-sensitive — safe in localStorage)
export const getUser = () => {
  const user = localStorage.getItem('user');
  if (!user) return null;
  try {
    return JSON.parse(user);
  } catch {
    localStorage.removeItem('user');
    return null;
  }
};
export const setUser = (user) => localStorage.setItem('user', JSON.stringify(user));
export const removeUser = () => localStorage.removeItem('user');

export const authAPI = {
  async register(username, email, password, fullName, role = 'user', phoneNumber = null) {
    const res = await api.post('/auth/register', {
      username, email, password, full_name: fullName, phone_number: phoneNumber, role
    });
    return res.data;
  },
  async login(username, password, totpCode = null) {
    const body = { username, password };
    if (totpCode) body.totp_code = totpCode;
    const res = await api.post('/auth/login', body);
    if (res.data.user) setUser(res.data.user);
    return res.data;
  },
  async logout() {
    try { await api.post('/auth/logout'); } catch { /* best-effort */ }
    removeUser();
  },
  async getCurrentUser() {
    const res = await api.get('/auth/me');
    return res.data;
  },
  isAuthenticated() {
    return !!getUser();
  },
  async updateProfile(profileData) {
    const res = await api.put('/auth/profile', profileData);
    setUser(res.data);
    return res.data;
  },
  async changePassword(currentPassword, newPassword) {
    const res = await api.put('/auth/change-password', {
      current_password: currentPassword, new_password: newPassword
    });
    return res.data;
  },
  // Legacy methods used by UserManagement.js and Settings.js
  async getUsers() {
    const res = await api.get('/users');
    return res.data;
  },
  async updateUser(id, data) {
    const res = await api.put(`/users/${id}`, data);
    return res.data;
  },
  async getSettings() {
    const res = await api.get('/settings');
    return res.data;
  },
  async updateSettings(settings) {
    const res = await api.put('/settings', settings);
    return res.data;
  },
  async testEmail(settings) {
    const res = await api.post('/settings/test-email', settings);
    return res.data;
  },
  async testSms(toNumber) {
    const res = await api.post('/sms/test', { to_number: toNumber });
    return res.data;
  },
  async getAuditLogs(filters = {}) {
    const params = new URLSearchParams();
    if (filters.user_id) params.append('user_id', filters.user_id);
    if (filters.resource_type) params.append('resource_type', filters.resource_type);
    if (filters.action) params.append('action', filters.action);
    if (filters.limit) params.append('limit', filters.limit);
    const res = await api.get(`/audit-logs?${params.toString()}`);
    return res.data;
  },
  async getResourceAuditLogs(type, id) {
    const res = await api.get(`/audit-logs/${type}/${id}`);
    return res.data;
  },
};

// User Management API (admin)
export const userAPI = {
  getAll: () => api.get('/users'),
  getById: (id) => api.get(`/users/${id}`),
  update: (id, data) => api.put(`/users/${id}`, data),
};

// Audit Log API
export const auditLogAPI = {
  getAll: (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.user_id) params.append('user_id', filters.user_id);
    if (filters.resource_type) params.append('resource_type', filters.resource_type);
    if (filters.action) params.append('action', filters.action);
    if (filters.limit) params.append('limit', filters.limit);
    return api.get(`/audit-logs?${params.toString()}`);
  },
  getByResource: (resourceType, resourceId) => api.get(`/audit-logs/${resourceType}/${resourceId}`),
};

// Settings API (admin)
export const settingsAPI = {
  get: () => api.get('/settings'),
  update: (settings) => api.put('/settings', settings),
  testEmail: (settings) => api.post('/settings/test-email', settings),
  testSms: (toNumber) => api.post('/sms/test', { to_number: toNumber }),
};

// Market Prices API (USDA MARS)
export const marketPriceAPI = {
  getPrices: () => api.get('/market-prices'),
  searchReports: (marketType) => api.get(`/market-prices/reports${marketType ? '?market_type=' + encodeURIComponent(marketType) : ''}`),
  getOffices: () => api.get('/market-prices/offices'),
};

// Commodity Prices API (APIFreaks)
export const commodityPriceAPI = {
  getPrices: (symbols) => api.get(`/commodity-prices${symbols ? '?symbols=' + encodeURIComponent(symbols) : ''}`),
  getHistorical: (symbol, date) => api.get(`/commodity-prices/historical?symbol=${encodeURIComponent(symbol)}&date=${encodeURIComponent(date)}`),
};

// Vaccination Protocol API
export const vaccinationProtocolAPI = {
  getAll: () => api.get('/vaccination-protocols'),
  getById: (id) => api.get(`/vaccination-protocols/${id}`),
  create: (data) => api.post('/vaccination-protocols', data),
  update: (id, data) => api.put(`/vaccination-protocols/${id}`, data),
  delete: (id) => api.delete(`/vaccination-protocols/${id}`),
  assign: (id, data) => api.post(`/vaccination-protocols/${id}/assign`, data),
};

// EPD Records API
export const epdRecordAPI = {
  getByCattle: (cattleId) => api.get(`/cattle/${cattleId}/epds`),
  create: (data) => api.post('/epd-records', data),
  update: (id, data) => api.put(`/epd-records/${id}`, data),
  delete: (id) => api.delete(`/epd-records/${id}`),
};

// BSE Records API
export const bseRecordAPI = {
  getByCattle: (cattleId) => api.get(`/cattle/${cattleId}/bse-records`),
  create: (data) => api.post('/bse-records', data),
  delete: (id) => api.delete(`/bse-records/${id}`),
};

// Pasture Condition API
export const pastureConditionAPI = {
  getByPasture: (pastureId) => api.get(`/pastures/${pastureId}/conditions`),
  create: (data) => api.post('/pasture-conditions', data),
  delete: (id) => api.delete(`/pasture-conditions/${id}`),
};

// Grazing Rotation API
export const grazingRotationAPI = {
  getAll: () => api.get('/grazing-rotations'),
  create: (data) => api.post('/grazing-rotations', data),
  update: (id, data) => api.put(`/grazing-rotations/${id}`, data),
  delete: (id) => api.delete(`/grazing-rotations/${id}`),
};

// Document API
export const documentAPI = {
  getAll: (params) => api.get('/documents', { params }),
  upload: (formData) => api.post('/documents', formData),
  download: (id) => api.get(`/documents/${id}/download`, { responseType: 'blob' }),
  delete: (id) => api.delete(`/documents/${id}`),
};

// Equipment Photo API
export const equipmentPhotoAPI = {
  getByEquipment: (equipmentId) => api.get(`/equipment/${equipmentId}/photos`),
  upload: (equipmentId, formData) => api.post(`/equipment/${equipmentId}/photos`, formData),
  getFile: (photoId) => api.get(`/equipment/photos/${photoId}`, { responseType: 'blob' }),
  delete: (photoId) => api.delete(`/equipment/photos/${photoId}`),
};

// Equipment Consumable (Fuel) API
export const equipmentConsumableAPI = {
  getByEquipment: (equipmentId) => api.get(`/equipment/${equipmentId}/consumables`),
  create: (data) => api.post('/equipment-consumables', data),
  delete: (id) => api.delete(`/equipment-consumables/${id}`),
};

// Notification Preference API
export const notificationPreferenceAPI = {
  get: () => api.get('/notification-preferences'),
  update: (data) => api.put('/notification-preferences', data),
};

// User Preference API (Dashboard widgets, table columns)
export const userPreferenceAPI = {
  get: () => api.get('/user-preferences'),
  update: (data) => api.put('/user-preferences', data),
};

// Bulk Operations API
export const bulkAPI = {
  movePasture: (data) => api.post('/bulk/move-pasture', data),
  vaccinate: (data) => api.post('/bulk/vaccinate', data),
  weight: (data) => api.post('/bulk/weight', data),
};

// Pedigree API
export const pedigreeAPI = {
  get: (cattleId, generations) => api.get(`/cattle/${cattleId}/pedigree${generations ? '?generations=' + generations : ''}`),
};

// Reports API
export const reportAPI = {
  animalHistory: (cattleId) => api.get(`/reports/animal-history/${cattleId}`),
  herdHealth: () => api.get('/reports/herd-health'),
};

// Advanced Analytics API
export const advancedAnalyticsAPI = {
  feedlotPerformance: (feedlotId) => api.get(`/analytics/feedlot-performance${feedlotId ? '?feedlot_id=' + feedlotId : ''}`),
  vetCosts: (year) => api.get(`/analytics/vet-costs${year ? '?year=' + year : ''}`),
  pastureUtilization: () => api.get('/analytics/pasture-utilization'),
  hayYield: (year) => api.get(`/analytics/hay-yield${year ? '?year=' + year : ''}`),
  breedingSeason: (year) => api.get(`/analytics/breeding-season${year ? '?year=' + year : ''}`),
  choreCompliance: (days) => api.get(`/analytics/chore-compliance${days ? '?days=' + days : ''}`),
  auditLogAnalytics: (days) => api.get(`/analytics/audit-log${days ? '?days=' + days : ''}`),
  hayInventory: (year) => api.get(`/analytics/hay-inventory${year ? '?year=' + year : ''}`),
};

// Calculator API
export const calculatorAPI = {
  stockingRate: (data) => api.post('/calculators/stocking-rate', data),
  stockingPresets: () => api.get('/calculators/stocking-rate/presets'),
  ration: (data) => api.post('/calculators/ration', data),
  rationPresets: () => api.get('/calculators/ration/presets'),
  adjustedWeight: (data) => api.post('/calculators/adjusted-weight', data),
  adg: (data) => api.post('/calculators/adg', data),
  breedingDueDate: (data) => api.post('/calculators/breeding-due-date', data),
  medicineDosage: (data) => api.post('/calculators/medicine-dosage', data),
  medicineDosagePresets: () => api.get('/calculators/medicine-dosage/presets'),
  hayNeeds: (data) => api.post('/calculators/hay-needs', data),
};

// Cattle Groups API
export const cattleGroupAPI = {
  getAll: () => api.get('/cattle-groups'),
  get: (id) => api.get(`/cattle-groups/${id}`),
  create: (data) => api.post('/cattle-groups', data),
  update: (id, data) => api.put(`/cattle-groups/${id}`, data),
  delete: (id) => api.delete(`/cattle-groups/${id}`),
  addMembers: (id, data) => api.post(`/cattle-groups/${id}/members`, data),
  removeMember: (id, cattleId) => api.delete(`/cattle-groups/${id}/members/${cattleId}`),
  getPerformance: (id) => api.get(`/cattle-groups/${id}/performance`),
  autoGenerate: (data) => api.post('/cattle-groups/auto-generate', data),
};

// Breeding Plans API
export const breedingPlanAPI = {
  getAll: () => api.get('/breeding-plans'),
  create: (data) => api.post('/breeding-plans', data),
  update: (id, data) => api.put(`/breeding-plans/${id}`, data),
  delete: (id) => api.delete(`/breeding-plans/${id}`),
};

// Saved Calculations API
export const savedCalculationAPI = {
  getAll: () => api.get('/saved-calculations'),
  save: (data) => api.post('/saved-calculations', data),
  delete: (id) => api.delete(`/saved-calculations/${id}`),
};

// Weather API
export const weatherAPI = {
  getCurrent: (lat, lon) => api.get('/weather/current', { params: { lat, lon } }),
};

// Sale Sheet API
export const saleSheetAPI = {
  get: (cattleId) => api.get(`/cattle/${cattleId}/sale-sheet`),
};

// Sire Comparison & Benchmarking
export const herdAnalyticsAPI = {
  sireComparison: (year) => api.get('/analytics/sire-comparison', { params: { year } }),
  herdBenchmarks: (years) => api.get('/analytics/herd-benchmarks', { params: { years } }),
  pastureRotationTimeline: (year) => api.get('/pasture-rotations/timeline', { params: { year } }),
};

// EPD Import/Compare
export const epdImportAPI = {
  import: (data) => api.post('/epd-records/import', data),
  compare: (data) => api.post('/epd-records/compare', data),
};

// 2FA API
export const twoFactorAPI = {
  setup: () => api.post('/auth/2fa/setup'),
  verify: (code) => api.post('/auth/2fa/verify', { code }),
  disable: (password) => api.post('/auth/2fa/disable', { password }),
};

// Admin API
export const adminAPI = {
  listBackups: () => api.get('/admin/backups'),
  createBackup: () => api.post('/admin/backups/create'),
  downloadBackup: (filename) => api.get(`/admin/backups/${encodeURIComponent(filename)}/download`, { responseType: 'blob' }),
  deleteBackup: (filename) => api.delete(`/admin/backups/${encodeURIComponent(filename)}`),
  getAuditLogs: (params) => api.get('/admin/audit-logs', { params }),
};

// Map API
export const mapAPI = {
  getPastures: () => api.get('/map/pastures'),
};

// Paginated Cattle API
export const paginatedCattleAPI = {
  get: (params) => api.get('/cattle/paginated', { params }),
};

// Breed Association / USDA Submission API
export const breedSubmissionAPI = {
  getAssociations: () => api.get('/breed-associations'),
  getAll: (status) => api.get(`/breed-submissions${status ? '?status=' + status : ''}`),
  create: (data) => api.post('/breed-submissions', data),
  update: (id, data) => api.put(`/breed-submissions/${id}`, data),
  delete: (id) => api.delete(`/breed-submissions/${id}`),
  generateRegistration: (data) => api.post('/breed-submissions/generate-registration', data),
  generateWeights: (data) => api.post('/breed-submissions/generate-weights', data),
  downloadAngusRegistrationPdf: (cattleId, params) => api.get(`/breed-submissions/angus-registration-pdf/${cattleId}`, { params, responseType: 'blob' }),
};

export default api;
